/** @file Document schema parts: animations (timelines per channel), direction sets, mix defaults. */
import { z } from 'zod';
import { names } from '../../names';
import { prim } from './primitives';
import { directionSet } from './directionSet';

/** Bone keys are setup-relative (§5.2); slot (rgb/alpha/dark) and ik keys are absolute and replace the setup value. */
const numKeys = z.array(prim.key(prim.finite));
const boolKeys = z.array(prim.discreteKey(z.boolean()));

const boneTimelines = z
  .object({
    rotation: numKeys.optional(),
    x: numKeys.optional(),
    y: numKeys.optional(),
    scaleX: numKeys.optional(),
    scaleY: numKeys.optional(),
    shearX: numKeys.optional(),
    shearY: numKeys.optional(),
    inherit: z.array(prim.discreteKey(prim.inherit)).optional(),
  })
  .strict();

const slotTimelines = z
  .object({
    attachment: z.array(prim.discreteKey(prim.name.nullable())).optional(),
    rgb: z.array(prim.key(prim.hex6)).optional(),
    alpha: z.array(prim.key(prim.unit)).optional(),
    dark: z.array(prim.key(prim.hex6)).optional(),
  })
  .strict();

const ikTimelines = z
  .object({ mix: numKeys.optional(), softness: numKeys.optional(), bendPositive: boolKeys.optional(), stretch: boolKeys.optional(), compress: boolKeys.optional() })
  .strict();

/**
 * Offsets are per vertex (2 numbers each) in mesh/bind space for rigid and skinned meshes alike; `start` is the
 * index into that flattened array (so always even). The compiler converts skinned ones to per-influence form.
 */
const deformKey = z
  .object({ f: prim.frame, curve: prim.curve.optional(), offsets: z.object({ start: z.number().int().nonnegative(), values: prim.numbers }).strict().nullable() })
  .strict();

const animation = z
  .object({
    name: prim.animationName,
    durationFrames: z.number().int().positive().optional(),
    bones: z.record(prim.idOf('bone'), boneTimelines),
    slots: z.record(prim.idOf('slot'), slotTimelines),
    ik: z.record(prim.idOf('ik'), ikTimelines),
    deform: z.record(prim.idOf('att'), z.array(deformKey)),
    drawOrder: z.array(z.object({ f: prim.frame, order: z.array(prim.idOf('slot')).nullable() }).strict()),
    events: z.array(
      z.object({ f: prim.frame, event: prim.idOf('evt'), int: z.number().int().optional(), float: prim.finite.optional(), string: z.string().optional() }).strict(),
    ),
  })
  .strict();

const drawOrderOp = z.discriminatedUnion('op', [
  z.object({ op: z.literal('swap'), a: prim.idOf('slot'), b: prim.idOf('slot') }).strict(),
  z.object({ op: z.literal('moveAbove'), slot: prim.idOf('slot'), ref: prim.idOf('slot') }).strict(),
  z.object({ op: z.literal('moveBelow'), slot: prim.idOf('slot'), ref: prim.idOf('slot') }).strict(),
]);

const directionEntry = z
  .object({
    mode: z.enum(['mirror', 'mirrorFix', 'authored']),
    source: prim.dir.optional(),
    flipX: z.boolean().optional(),
    fix: z
      .object({ skin: prim.idOf('skin').optional(), drawOrderOps: z.array(drawOrderOp).optional(), animations: z.record(prim.name, prim.idOf('anim')).optional() })
      .strict()
      .optional(),
  })
  .strict();

const directions = directionSet(directionEntry);

export const animationParts = {
  animation,
  directions,
  /** `"walk>run": seconds` */
  mixDefaults: z.record(z.string().regex(names.MIX_PAIR_RE, 'expected "<animation>><animation>"'), prim.finite.nonnegative()),
};

export type AnimationDoc = z.infer<typeof animation>;
export type BoneTimelines = z.infer<typeof boneTimelines>;
export type SlotTimelines = z.infer<typeof slotTimelines>;
export type IkTimelines = z.infer<typeof ikTimelines>;
export type DeformKey = z.infer<typeof deformKey>;
export type DrawOrderOp = z.infer<typeof drawOrderOp>;
export type DirectionEntry = z.infer<typeof directionEntry>;
export type DirectionsDoc = z.infer<typeof directions>;
